/**
 * File: saga.js
 * Project: EXTA
 * File Created: Thursday, 25th April 2024 10:56:45 am
 * Author: Jorge Sierra Blázquez (jorge.sierra@aerin.es)
 * Version: 1.0.0
 * -----
 * Last Modified: Friday, 18th October 2024 10:46:31 am
 * Modified By: Ricardo Fernández Cuesta
 * -----
 * Copyright (c) 2023 - 2024 Aerin Sistemas S. L. copying, distribution or modification not authorised in writing is prohibited.
 */

import { all, call, fork, put, takeEvery } from 'redux-saga/effects';
import axios from 'axios';

import {
  LOGIN_USER,
  LOAD_USER,
  REGISTER_USER,
  LOGOUT_USER,
  FORGOT_PASSWORD,
  RESET_PASSWORD,
} from '../contants';

import {
  loginUserSuccess,
  loginUserError,
  loadUserSuccess,
  loadUserError,
  registerUserSuccess,
  registerUserError,
  forgotPasswordSuccess,
  forgotPasswordError,
  resetPasswordSuccess,
  resetPasswordError,
} from './actions';

import { adminRoot, loginRoot } from '../../constants/defaultValues';
import { setCurrentUser, setAuthToken } from '../../helpers/Utils';

export function* watchLoginUser() {
  // eslint-disable-next-line no-use-before-define
  yield takeEvery(LOGIN_USER, loginWithUsernamePassword);
}

const loginWithUsernamePasswordAsync = async (username, password) => {
  const { REACT_APP_URL_LOGIN } = process.env;
  try {
    // Se realiza la petición para iniciar sesión
    const response = await axios.post(REACT_APP_URL_LOGIN, {
      username,
      password,
    });
    return response;
  } catch (error) {
    if (error.response) {
      // El servidor respondió con un status code fuera del rango 2xx
      if (error.response.status >= 500) {
        // Error interno del servidor o problema en la API
        throw new Error(
          `${error.response.status} - Internal server error. Please try again later.`
        );
      } else if (
        error.response.status === 401 ||
        error.response.status === 403 ||
        error.response.status === 400
      ) {
        // Credenciales incorrectas o acceso no autorizado
        throw new Error(
          `${error.response.status} - Invalid credentials. Please check your username and password.`
        );
      } else {
        // Otros errores (por ejemplo, validaciones)
        throw new Error(
          error.response.data?.message || 'An error occurred. Please try again.'
        );
      }
    } else if (error.request) {
      // La petición se realizó pero no se recibió respuesta
      throw new Error(
        'No response from server. Please check your connection or try again later.'
      );
    } else {
      // Algo ocurrió al configurar la solicitud
      throw new Error(`Error configuring the request: ${error.message}`);
    }
  }
};

function* loginWithUsernamePassword({ payload }) {
  const { username, password } = payload.user;
  const { history } = payload;

  // Se elimina cualquier token de autorización anterior
  if (
    axios.defaults.headers.common &&
    axios.defaults.headers.common.Authorization
  ) {
    delete axios.defaults.headers.common.Authorization;
  }

  try {
    const response = yield call(
      loginWithUsernamePasswordAsync,
      username,
      password
    );
    const { user, token } = response.data;

    // Configura el usuario y el token actual
    setCurrentUser(user);
    setAuthToken(token, user.is_superuser);
    axios.defaults.headers.common.Authorization = `Token ${token}`;

    yield put(loginUserSuccess(response.data));
    history.push(adminRoot);
  } catch (error) {
    console.error('Error en el login:', error);
    yield put(loginUserError(error.message));
  }
}

export function* watchLoadUser() {
  // eslint-disable-next-line no-use-before-define
  yield takeEvery(LOAD_USER, loadUser);
}

const loadUserAsync = async () =>
  // eslint-disable-next-line no-return-await
  axios
    .get('/api/auth/user/')
    .then((user) => user)
    .catch((error) => error);

function* loadUser({ payload }) {
  const { history } = payload;
  try {
    const loginUser = yield call(loadUserAsync);
    if (!loginUser.message) {
      yield put(loadUserSuccess(loginUser.data));
    } else {
      const message = 'Expired session token.';
      yield put(loadUserError(message));
      history.push(loginRoot);
    }
  } catch (error) {
    yield put(loadUserError(error));
    history.push(loginRoot);
  }
}

export function* watchRegisterUser() {
  // eslint-disable-next-line no-use-before-define
  yield takeEvery(REGISTER_USER, registerWithEmailPassword);
}

const registerWithEmailPasswordAsync = async () => {
  // eslint-disable-next-line no-return-await
  /*
  await auth
    .createUserWithEmailAndPassword(email, password)
    .then((user) => user)
    .catch((error) => error);
  */
};

function* registerWithEmailPassword() {
  const { email, password } = '';
  const { history } = '';
  try {
    const registerUser = yield call(
      registerWithEmailPasswordAsync,
      email,
      password
    );
    if (!registerUser.message) {
      const item = { uid: registerUser.user.uid };
      setCurrentUser(item);
      yield put(registerUserSuccess(item));
      history.push(adminRoot);
    } else {
      yield put(registerUserError(registerUser.message));
    }
  } catch (error) {
    yield put(registerUserError(error));
  }
}

export function* watchLogoutUser() {
  // eslint-disable-next-line no-use-before-define
  yield takeEvery(LOGOUT_USER, logout);
}

const logoutAsync = async () => {
  const { REACT_APP_URL_LOGOUT } = process.env;
  return axios
    .post(REACT_APP_URL_LOGOUT, {})
    .then((response) => response)
    .catch((error) => error);
};

// eslint-disable-next-line no-unused-vars
function* logout({ payload }) {
  // const { history } = payload;

  try {
    yield call(logoutAsync);
  } catch (error) {
    // El console log está para evitar dejar el catch vacío.
    console.log('Logout error.');
  } finally {
    setCurrentUser(null);
    setAuthToken(null, null);
    if (
      typeof axios.defaults.headers.common === 'object' &&
      'Authorization' in axios.defaults.headers.common
    ) {
      delete axios.defaults.headers.common.Authorization;
    }
    // history.push(loginRoot);
    window.location.href = loginRoot;
  }
}

export function* watchForgotPassword() {
  // eslint-disable-next-line no-use-before-define
  yield takeEvery(FORGOT_PASSWORD, forgotPassword);
}

const forgotPasswordAsync = async () => {
  // eslint-disable-next-line no-return-await
  /*
  return await auth
    .sendPasswordResetEmail(email)
    .then((user) => user)
    .catch((error) => error);
  */
};

function* forgotPassword({ payload }) {
  const { email } = payload.forgotUserMail;
  try {
    const forgotPasswordStatus = yield call(forgotPasswordAsync, email);
    if (!forgotPasswordStatus) {
      yield put(forgotPasswordSuccess('success'));
    } else {
      yield put(forgotPasswordError(forgotPasswordStatus.message));
    }
  } catch (error) {
    yield put(forgotPasswordError(error));
  }
}

export function* watchResetPassword() {
  // eslint-disable-next-line no-use-before-define
  yield takeEvery(RESET_PASSWORD, resetPassword);
}

const resetPasswordAsync = async () => {
  // eslint-disable-next-line no-return-await
  /*
  return await auth
    .confirmPasswordReset(resetPasswordCode, newPassword)
    .then((user) => user)
    .catch((error) => error);
  */
};

function* resetPassword({ payload }) {
  const { newPassword, resetPasswordCode } = payload;
  try {
    const resetPasswordStatus = yield call(
      resetPasswordAsync,
      resetPasswordCode,
      newPassword
    );
    if (!resetPasswordStatus) {
      yield put(resetPasswordSuccess('success'));
    } else {
      yield put(resetPasswordError(resetPasswordStatus.message));
    }
  } catch (error) {
    yield put(resetPasswordError(error));
  }
}

export default function* rootSaga() {
  yield all([
    fork(watchLoginUser),
    fork(watchLogoutUser),
    fork(watchLoadUser),
    fork(watchRegisterUser),
    fork(watchForgotPassword),
    fork(watchResetPassword),
  ]);
}
